import { useQuery } from "@tanstack/react-query";

import {
  GraphQLAPI,
  parseExamConfig,
  queries,
  fragments,
} from "@examspark/api";
import { SystemTag } from "@examspark/common";
import {
  Resource,
  SearchableConnection,
  SearchableResourceFilterInput,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { usePaginatedSearch } from "../../hooks/usePaginatedSearch";
import { useAppContext } from "../../providers/AppContext";

export const useQuerySearchExamConfigs = (params?: {
  enabled?: boolean;
  filter?: Partial<{
    self: boolean;
    ltiContextId: string;
    requireClassroom: boolean;
    classroomId: string;
    unitId: string;
    name: string;
  }>;
}) => {
  const authorizationContext = useAuthorizationContext();
  const [state] = useAppContext();

  return usePaginatedSearch({
    queryKey: ["user-exam-configs", state.user?.id, params?.filter],
    enabled: params?.enabled !== false && !!state.user?.id,
    limit: 10,
    queryFn: async (context) => {
      const filter: SearchableResourceFilterInput = {
        sk: {
          eq: "exam-config",
        },
        workspaceId: {
          eq: state.workspaceUser?.workspace?.id,
        },
        isDeleted: {
          ne: true,
        },
      };

      if (params?.filter?.self) {
        filter.creatorId = { eq: state.user?.id };
      }
      if (params?.filter?.ltiContextId) {
        if (!filter.and) filter.and = [];
        filter.and.push({
          tags: {
            eq: `${SystemTag.LtiContextId}#${params.filter.ltiContextId}`,
          },
        });
      }

      if (params?.filter?.classroomId) {
        if (!filter.and) filter.and = [];
        filter.and.push({
          tags: {
            eq: `classroom-id#${params.filter.classroomId}`,
          },
        });
      } else if (params?.filter?.requireClassroom) {
        if (!filter.and) filter.and = [];
        filter.and.push(
          {
            tags: {
              notEmpty: "classroom-id",
            },
          },
          {
            tags: {
              exists: `classroom-id`,
            },
          },
        );
      }

      if (params?.filter?.unitId) {
        if (!filter.and) filter.and = [];
        filter.and.push({
          tags: {
            eq: `classroom-unit-id#${params.filter.unitId}`,
          },
        });
      }
      if (params?.filter?.name) {
        filter.name = { matchPhrase: params.filter.name };
      }

      const result = await GraphQLAPI.query<{
        search: SearchableConnection<Resource>;
      }>({
        query: queries.search.search({
          label: "SearchExamConfigs",
          fields: fragments.exam.examConfigFragment,
        }),
        variables: {
          authorizationContext,
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          ...(context.meta?.variables ?? {}),
          filter,
        },
      });

      return result?.data?.search;
    },
  });
};

export const useQueryGetExamConfigForReport = (
  examConfigId: string | undefined,
) => {
  return useQuery({
    enabled: !!examConfigId,
    queryKey: ["exam-config", examConfigId],
    queryFn: async () => {
      const result = (
        (await GraphQLAPI.query({
          query: queries.exam.getExamConfigQueryLegacy,
          variables: {
            id: examConfigId,
            sk: "exam-config",
          },
        })) as any
      ).data.getResource;

      return parseExamConfig(result);
    },
  });
};
